import { ApolloProvider } from '@apollo/client';
// Import global CSS before App to ensure any library CSS is specified in the correct order
import '../global.scss';
import 'core-js/stable';
import React from 'react';
import ReactDOM from 'react-dom';
import { createBrowserHistory } from 'history';
import { Router } from 'react-router-dom';
import { loadableReady } from '@loadable/component';
import { FeatureFlagProvider, FeatureFlags } from '@moda/portal-stanchions';
import { loadSegment } from '@moda/portal-astrarium';
import { HelmetProvider } from 'react-helmet-async';
import { identifyUser } from '../lib/identifyUser';
import { trackFeatureFlagView, clearFeatureFlagTrackingCache } from '../lib/trackFeatureFlagView';
import { App } from '../App';
import { createClientSideApolloClient } from '../lib/apollo/client';
import { isDevelopment } from '../lib/isEnv';
import { CONFIG } from '../config';
import '../../vendor/crownpeak';
import { getUserFromCookies } from '../hooks/useUser';
// TODO: move this polyfill to it's own chunk and load it only when required
import 'intersection-observer';
import 'isomorphic-fetch';

declare global {
  interface Window {
    __FEATURE_FLAGS__?: FeatureFlags;
    __SERVER_ERROR__?: boolean;
  }
}

const el = document.getElementById('root');

const history = createBrowserHistory();
const client = createClientSideApolloClient(history);

history.listen(() => clearFeatureFlagTrackingCache());

// If the user has declined the evidon prompt, don't load GTM, but load everything else.
// If the user has accepted the evidon prompt, or has not decided yet, load everything.
// If the user declines, the page will hard-refresh (in the evidon callback),
// and we'll see the decline cookie on reload.
// If the user closes the prompt without answering, it will stay hidden until the next refresh,
// but is otherwise effectively the same as accepting.
// We need to do it this way, because there's no way to unload an integration
// once Segment loads it.
if (document.cookie.includes('_evidon_suppress_notification_cookie')) {
  loadSegment(CONFIG.SEGMENT_WRITE_KEY, {
    integrations: {
      All: false,
      'Segment.io': true,
      'Google Analytics': true
    }
  });
} else {
  loadSegment(CONFIG.SEGMENT_WRITE_KEY, { integrations: { All: true } });
}

identifyUser(getUserFromCookies());

const ClientSideApp = (
  <FeatureFlagProvider
    flags={window.__FEATURE_FLAGS__ || {}}
    onFlagRequested={trackFeatureFlagView}
  >
    <Router history={history}>
      <ApolloProvider client={client}>
        <HelmetProvider>
          <App error={window.__SERVER_ERROR__} />
        </HelmetProvider>
      </ApolloProvider>
    </Router>
  </FeatureFlagProvider>
);

const init = () =>
  isDevelopment()
    ? ReactDOM.render(ClientSideApp, el)
    : loadableReady(() => ReactDOM.hydrate(ClientSideApp, el));

init();
